import React from 'react'
import MediaQuery from 'react-responsive'

import { AcornsIcon } from '@acorns/icons'
import { useRemoteStrings } from '@acorns/web-utils'
import styled from 'styled-components'

import Spend3DCard from 'assets/images/spend-3d-angled.png'
import SpendCardSVG from 'assets/images/spend-angled.svg'
import useWindowDimensions from 'hooks/useWindowDimensions'
import themeValues, { style } from 'theme'
import { CHECKBOX } from 'utils/testing/testIDs'

import {
  StyledBody,
  StyledCard,
  StyledCloseAccountButton,
  StyledHeading,
} from '../components'
import { MergedProps } from './index'

const ButtonContainer = styled.div`
  margin-top: auto;
`

const StyledSpendCard = styled(StyledCard)`
  padding-top: 0px;
`

const WVCard = styled(StyledCard)<{ innerHeight?: number }>`
  height: ${(props) => `calc(${props.innerHeight}px - 80px)`};
  &&& {
    border-radius: 0;
  }
`

const SpendBodyContainer = styled.div`
  z-index: 1;
  position: relative;
  top: -325px;
  padding-left: 50px;
  text-align: left;
`
const BodyContainer = styled.div`
  overflow: hidden;
  height: 250px;
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    min-height: 50%;
    align-self: center;
    padding-top: 50px;
  }
`
const CardContainer = styled.div`
  max-width: 500px;
  height: 400px;
  overflow: hidden;
  position: relative;
  right: -40%;
  top: -60px;
  z-index: 1;
`
const StyledSpendBody = styled(StyledBody)`
  text-align: left;
  margin: 10px 0 0 0;
`
const SpendCardImg = styled.img`
  max-width: 500px;
  position: absolute;
  top: 0;
  right: 0;
  transform: translateY(-100%) rotate(60deg); /* W3C */
  transform-origin: left bottom;
`

const AcknowledgementContainer = styled.div`
  border-top: 1px solid ${style((theme) => theme.colors.ivory)};
  border-radius: 0px;
  display: flex;
  justify-content: space-between;
  padding: 50px 50px 0 50px;
`

const StyledRedButton = styled<MergedProps, any>(StyledCloseAccountButton)`
  ${({ disabled }) => !disabled && 'background-color: red !important;'}
  margin: 0;
  align-self: flex-end;
  margin-top: 0px;
  width: 150px;
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    width: 100%;
  }
`

const StyledGreenButton = styled(StyledCloseAccountButton)`
  margin: 0;
  align-self: flex-end;
  margin-top: 0px;
  width: 150px;
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    width: 100%;
  }
`
const WVMobileSpendCardImg = styled.div`
  margin-top: 50px;
  @media screen and (max-width: 321px) {
    margin-top: 0;
  }
`

const MobileSpendCardImg = styled.img`
  margin: 0 auto;
  max-width: 210px;
`
const Label = styled.div`
  margin-right: auto;
  line-height: 22px;
  font-size: 14px;
  color: ${style((theme) => theme.colors.slate)};
  max-width: 379px;
  text-align: left;
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    font-size: 12px;
    line-height: 16px;
    margin-bottom: 20px;
  }
`

const ChecklistItemEmptyCheck = styled.div`
  margin-right: 10px;
  box-sizing: border-box;
  border-radius: 50%;
  height: 22px;
  width: 22px;
  border: 1px solid ${style((theme) => theme.colors.stoneLight)};
`

const ChecklistItemCheckContainer = styled(ChecklistItemEmptyCheck)`
  border: none;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${style((theme) => theme.colors.green)};
`

const CheckContainer = styled.div`
  display: flex;
  flex-direction: row;
`
const MobileCheckInputContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
`

export const RequestScreen: React.FC<MergedProps> = ({
  spendBalance,
  handleCheckboxChange,
  checked,
  handleAcknowledgeDisclaimer,
  isMultiStepClosureFlow,
  isWebView,
}) => {
  const CardComponent = isWebView ? WVCard : StyledCard
  const { height } = useWindowDimensions()
  const { getRemoteString } = useRemoteStrings()
  return (
    <>
      <MediaQuery minWidth={themeValues.breakpoints.sm}>
        <StyledSpendCard>
          <BodyContainer>
            <CardContainer>
              <SpendCardImg src={SpendCardSVG} />
            </CardContainer>
            <SpendBodyContainer>
              <StyledHeading>Just a heads up</StyledHeading>
              <StyledSpendBody>
                {getRemoteString({
                  key: 'spend.close_account.request_screen.disclaimer_message',
                })}
              </StyledSpendBody>
            </SpendBodyContainer>
          </BodyContainer>

          <AcknowledgementContainer>
            <CheckContainer
              data-testid={CHECKBOX}
              onClick={handleCheckboxChange}
            >
              {checked ? (
                <ChecklistItemCheckContainer>
                  <AcornsIcon
                    icon={AcornsIcon.Icon.ThickCheckmark}
                    width="12px"
                  />
                </ChecklistItemCheckContainer>
              ) : (
                <ChecklistItemEmptyCheck />
              )}
            </CheckContainer>
            <Label>
              {getRemoteString({
                key: 'spend.close_account.request_screen.acknowledgement_message',
              })}
            </Label>
            {spendBalance > 0 || isMultiStepClosureFlow ? (
              <StyledGreenButton
                onClick={handleAcknowledgeDisclaimer}
                disabled={!checked}
              >
                Continue
              </StyledGreenButton>
            ) : (
              <StyledRedButton
                onClick={handleAcknowledgeDisclaimer}
                disabled={!checked}
              >
                Close account
              </StyledRedButton>
            )}
          </AcknowledgementContainer>
        </StyledSpendCard>
      </MediaQuery>
      <MediaQuery maxWidth={themeValues.breakpoints.sm - 1}>
        <CardComponent innerHeight={isWebView ? height : null}>
          <div>
            <WVMobileSpendCardImg>
              <MobileSpendCardImg src={Spend3DCard} />
            </WVMobileSpendCardImg>
            <StyledHeading>Just a heads up</StyledHeading>
            <StyledBody>
              {getRemoteString({
                key: 'spend.close_account.request_screen.disclaimer_message',
              })}
            </StyledBody>
          </div>
          <ButtonContainer>
            <MobileCheckInputContainer>
              <CheckContainer
                data-testid={CHECKBOX}
                onClick={handleCheckboxChange}
              >
                {checked ? (
                  <ChecklistItemCheckContainer>
                    <AcornsIcon
                      icon={AcornsIcon.Icon.ThickCheckmark}
                      width="12px"
                    />
                  </ChecklistItemCheckContainer>
                ) : (
                  <ChecklistItemEmptyCheck />
                )}
              </CheckContainer>
              <Label>
                {getRemoteString({
                  key: 'spend.close_account.request_screen.acknowledgement_message',
                })}
              </Label>
            </MobileCheckInputContainer>
            {spendBalance > 0 || isMultiStepClosureFlow ? (
              <StyledGreenButton
                onClick={handleAcknowledgeDisclaimer}
                disabled={!checked}
              >
                Continue
              </StyledGreenButton>
            ) : (
              <StyledRedButton
                onClick={handleAcknowledgeDisclaimer}
                disabled={!checked}
              >
                Close account
              </StyledRedButton>
            )}
          </ButtonContainer>
        </CardComponent>
      </MediaQuery>
    </>
  )
}
